import React, { useState } from "react";
import {
  BottomSheet,
  Button,
  Modal,
  TextField,
  useIsMobile,
} from "../../../components/ui";

/**
 * What has to be typed to arm the button.
 *
 * Upper case, and matched exactly: a word that has to be copied letter for
 * letter cannot be got through by pressing Enter twice on a dialog somebody
 * thought said something else. It is the account, the client list, every plan
 * written for them and the whole revenue history — there is no undo behind this
 * and no support call that puts it back.
 */
const CONFIRM_WORD = "DELETE";

interface DeleteAccountModalProps {
  open: boolean;
  /** True while the deletion is with the server — the dialog stands down. */
  deleting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * The dialog behind Delete account: what goes, and the word that proves it was
 * meant.
 *
 * It states the losses as a list rather than a sentence. "Your account and all
 * its data" is the kind of phrase that gets skimmed; named things — the
 * clients, the plans, the revenue, the branding, the account itself — are what
 * make somebody stop and think about the one they had not considered.
 */
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  deleting = false,
  onClose,
  onConfirm,
}) => {
  const [typed, setTyped] = useState("");

  // Which shell the question is asked in. Not a `md:` class: a centred dialog
  // and a sheet that comes up from the bottom edge are two different elements
  // with two different ways in and out, not one panel with different corners.
  // The same call `ServiceFormModal` makes.
  const isMobile = useIsMobile();

  const armed = typed.trim() === CONFIRM_WORD;

  const close = () => {
    // Reopening starts from an empty box: a dialog that remembers the word is
    // a dialog whose safeguard only works once.
    setTyped("");
    onClose();
  };

  // Never while the deletion is with the server: the dialog is what is holding
  // the screen still until it answers.
  const dismiss = () => {
    if (!deleting) close();
  };

  /** The same two buttons whichever shell is drawn — the sheet pins them under
   *  its body, the dialog puts them in its footer row. */
  const actions = (
    <>
      <Button variant="secondary" size="md" disabled={deleting} onClick={close}>
        Cancel
      </Button>
      <Button
        variant="danger"
        size="md"
        disabled={!armed}
        loading={deleting}
        onClick={onConfirm}
      >
        Delete account
      </Button>
    </>
  );

  const body = (
    <>
      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        Deleting your account is permanent. You&rsquo;ll lose access to your
        workspace and all data associated with it, including:
      </p>
      <ul className="mt-2 list-disc pl-5 text-[14px] 3xl:text-[15px] leading-[1.7] text-ink-muted">
        <li>Your account and workspace</li>
        <li>All clients and enquiries</li>
        <li>All meal plans</li>
        <li>Revenue and invoice history</li>
        <li>Your saved branding details</li>
      </ul>
      <p className="mt-3 text-[14px] 3xl:text-[15px] font-medium text-ink">
        Once deleted, this data cannot be recovered.
      </p>

      <TextField
        label={`To permanently delete your account, type ${CONFIRM_WORD} below.`}
        className="mt-4"
        placeholder={CONFIRM_WORD}
        autoComplete="off"
        // Never guessed at for the user: the whole point is that it is typed.
        spellCheck={false}
        disabled={deleting}
        value={typed}
        onValueChange={setTyped}
      />
    </>
  );

  // A phone gets the sheet. This one asks for a word to be typed, so the
  // keyboard is coming up whatever holds it — and a panel floating in the
  // middle of the screen is the worst place to be standing when it does: it is
  // shoved up the screen, or half of it goes behind the keys. A sheet is
  // already at the bottom edge, its own scroll keeps the box above them, and
  // the two answers stay pinned under the thumb that has to give one. What is
  // asked is identical either way; only what holds it changes.
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        title="Delete your account"
        onClose={dismiss}
        footer={actions}
      >
        <div className="px-5 pb-2 pt-2">{body}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      title="Delete your account"
      onClose={dismiss}
      className="max-w-[580px]"
      footer={actions}
    >
      {body}
    </Modal>
  );
};

export default DeleteAccountModal;
